import { IconPlus } from "@tabler/icons-react";
import { RiBookOpenLine } from "@remixicon/react";
import { useGlobalStore } from "@/GlobalStore";
import DiagonalShimmer from "@/components/common/loaders/DiagonalShimmer";
import {
  createCreativeLibraryItem,
  deleteCreativeLibraryItem,
  fetchCreativeLibrary,
} from "../../apiService";
import { withCacheBuster } from "@/components/common/adCard/Media";

export type CreativeLibraryItem = {
  id: string;
  file_url: string;
  naturalWidth?: number;
  naturalHeight?: number;
  isUploading?: boolean;
};

type Props = {
  creativeLibrary: CreativeLibraryItem[] | null;
  setCreativeLibrary: (
    updater:
      | CreativeLibraryItem[]
      | null
      | ((prev: CreativeLibraryItem[] | null) => CreativeLibraryItem[] | null),
  ) => void;
  isLoadingCreativeLibrary: boolean;
  onImageSelect: (params: {
    token: number;
    src: string;
    naturalWidth: number;
    naturalHeight: number;
  }) => void;
  nextAddImageToken: number;
  setNextAddImageToken: (updater: number | ((prev: number) => number)) => void;
};

const CreativeLibraryGallery = ({
  creativeLibrary,
  setCreativeLibrary,
  isLoadingCreativeLibrary,
  onImageSelect,
  nextAddImageToken,
  setNextAddImageToken,
}: Props) => {
  return (
    <div className="mt-5">
      <div className="mb-1 flex items-center gap-[6px]">
        <RiBookOpenLine size={14} className="text-brand-primary-400 shrink-0" />
        <span className="text-[12px] text-brand-primary-800">Library</span>
      </div>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {/* Add logo tile - First position */}
        <button
          className="relative w-full aspect-square rounded overflow-hidden border border-dashed border-gray-300 hover:border-gray-400 flex items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*";
            input.multiple = true; // Allow multiple file selection
            input.onchange = () => {
              const files = Array.from(input.files || []);
              if (files.length === 0) return;

              const accountId = String(
                useGlobalStore.getState().selectedAccount?.id || "",
              );
              if (!accountId) return;

              // Process each file independently
              files.forEach((file) => {
                void (async () => {
                  const tempId = `temp-${Date.now()}-${Math.random()
                    .toString(36)
                    .slice(2, 9)}`;

                  try {
                    // Insert shimmer placeholder at the start for this file
                    setCreativeLibrary((prev) => {
                      const next = Array.isArray(prev) ? [...prev] : [];
                      next.unshift({
                        id: tempId,
                        file_url: "",
                        isUploading: true,
                      });
                      return next;
                    });
                    const res = await createCreativeLibraryItem(accountId, file);
                    const created = res.data;
                    if (created) {
                      // Replace only the specific temp placeholder with the real item
                      setCreativeLibrary((prev) => {
                        const list = Array.isArray(prev) ? [...prev] : [];
                        return list.map((l) =>
                          l.id === tempId
                            ? {
                                id: created.id,
                                file_url: created.file_url,
                              }
                            : l,
                        );
                      });
                    } else {
                      // If no created item returned, remove this specific temp placeholder
                      setCreativeLibrary((prev) => {
                        const list = Array.isArray(prev) ? prev : [];
                        return list.filter((l) => l.id !== tempId);
                      });
                    }
                  } catch (error) {
                    console.error("Failed to upload creative library item:", error);
                    // If failed, remove only this specific temp placeholder
                    setCreativeLibrary((prev) => {
                      const list = Array.isArray(prev) ? prev : [];
                      return list.filter((l) => l.id !== tempId);
                    });
                  }
                })();
              });
            };
            input.click();
          }}
          title="Add logo"
        >
          <IconPlus className="w-5 h-5 text-gray-500" />
        </button>

        {/* Loading shimmer */}
        {isLoadingCreativeLibrary && (
          <div className="relative w-full aspect-square rounded overflow-hidden border border-gray-300">
            <DiagonalShimmer colors={["#F6F6F6", "#BFBFBF", "#FFFFFF"]} duration={3} />
          </div>
        )}

        {/* Existing creative library items */}
        {creativeLibrary?.map((logo) => (
          <div
            key={logo.id}
            className="relative group w-full aspect-square rounded overflow-hidden border border-gray-300 hover:border-gray-400 bg-[rgb(201,201,201)]"
          >
            {/* Delete button */}
            {!logo.isUploading && (
              <button
                className="absolute top-[0.5px] right-[0.5px] z-20 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity duration-150 w-4 h-4 flex items-center justify-center"
                title="Delete"
                aria-label="Delete logo"
                onClick={(e) => {
                  void (async () => {
                    e.stopPropagation();
                    try {
                      const accountId = String(
                        useGlobalStore.getState().selectedAccount?.id || "",
                      );
                      if (!accountId) return;
                      // Optimistically remove
                      setCreativeLibrary((prev) =>
                        (prev || []).filter((l) => l.id !== logo.id),
                      );
                      await deleteCreativeLibraryItem(accountId, logo.id);
                    } catch {
                      // On failure, refetch list
                      const accountId = String(
                        useGlobalStore.getState().selectedAccount?.id || "",
                      );
                      if (accountId) {
                        const response = await fetchCreativeLibrary(accountId);
                        setCreativeLibrary(
                          (response.data || []).map((item) => ({
                            id: item.id,
                            file_url: item.file_url,
                          })),
                        );
                      }
                    }
                  })();
                }}
              >
                {/* Use a plain SVG fallback for reliable render */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V6ZM18 8H6V20H18V8ZM9 11H11V17H9V11ZM13 11H15V17H13V11ZM9 4V6H15V4H9Z"></path>
                </svg>
              </button>
            )}

            <button
              className="absolute inset-0 z-10"
              onClick={(e) => {
                if (logo.isUploading) return;
                e.stopPropagation();
                // Use cached dimensions instead of downloading image again
                setNextAddImageToken((t) => t + 1);
                onImageSelect({
                  token: nextAddImageToken + 1,
                  src: logo.file_url,
                  naturalWidth: logo.naturalWidth || 200, // Use cached or default
                  naturalHeight: logo.naturalHeight || 200, // Use cached or default
                });
              }}
              disabled={Boolean(logo.isUploading)}
            />

            {logo.isUploading ? (
              <DiagonalShimmer colors={["#F6F6F6", "#BFBFBF", "#FFFFFF"]} duration={3} />
            ) : (
              <img
                crossOrigin="anonymous"
                src={withCacheBuster(logo.file_url)}
                alt="creative library item"
                className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                draggable={false}
                onLoad={(e) => {
                  // Cache dimensions when preview loads
                  const img = e.currentTarget;
                  setCreativeLibrary(
                    (prev) =>
                      prev?.map((item) =>
                        item.id === logo.id
                          ? {
                              ...item,
                              naturalWidth: img.naturalWidth,
                              naturalHeight: img.naturalHeight,
                            }
                          : item,
                      ) || null,
                  );
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CreativeLibraryGallery;
